// 9fbef606107a605d69c0edbcd8029e5d
/* eslint-disable max-statements */
/* eslint-disable complexity */
import { call, put, putResolve, takeLatest, takeLeading, select } from 'redux-saga/effects';
import logger from '@tcp/core/src/utils/loggerInstance';
import { setPlpProductsDataOnServer } from '@tcp/core/src/components/features/browse/ProductListing/container/ProductListing.actions';
import { getAPIConfig, setBossBopisEnabled } from '@tcp/core/src/utils';
import { errorMessages } from '@tcp/core/src/services/api.constants';
import {
  setBossBopisFlags,
  setLastUpdated,
  setToken,
  setServerTime,
} from '@tcp/core/src/reduxStore/actions';
import { validateReduxCache } from '@tcp/core/src/utils/cache.util';
import bootstrapAbstractor from '../../services/abstractors/bootstrap';
import tokenAbstractor from '../../services/abstractors/bootstrap/token';

import NavigationAbstractor from '../../services/abstractors/bootstrap/navigation';
import xappAbstractor from '../../services/abstractors/bootstrap/xappConfig';
import lastUpdatedAbstractor from '../../services/abstractors/bootstrap/lastUpdated';
import { API_CONFIG } from '../../services/config';
import {
  loadLayoutData,
  loadLabelsData,
  loadModulesData,
  loadXappConfigData,
  loadXappConfigDataOtherBrand,
  setDeviceInfo,
  setCountry,
  setCurrency,
  getSetTcpSegment,
  setLanguage,
  setBootstrapError,
} from '../actions';
import { loadHeaderData } from '../../components/common/organisms/Header/container/Header.actions';
import { loadFooterData } from '../../components/common/organisms/Footer/container/Footer.actions';
import { loadNavigationData } from '../../components/features/content/Navigation/container/Navigation.actions';
import GLOBAL_CONSTANTS from '../constants';
import { parseBoolean } from '../../utils';
import { generateSessionId, readCookie, setCookie } from '../../utils/cookie.util';
import {
  defaultBrand,
  defaultChannel,
  defaultCountry,
  MobileChannel,
} from '../../services/api.constants';

const { sessionCookieKey } = API_CONFIG;

const errorHandler = (errorArgs) => {
  const { errorMsg, payload, component, errorObject = '' } = errorArgs;
  logger.error({
    error: errorMsg,
    errorTags: [`Bootstrap Saga`, component],
    extraData: {
      ...payload,
      errorObject,
      [sessionCookieKey]: generateSessionId(true),
    },
  });
};

// TODO - GLOBAL-LABEL-CHANGE - STEP 1.3 - Uncomment these references
// import GLOBAL_CONSTANTS, { LABELS } from '../constants';
// import { loadLayoutData, loadLabelsData, setLabelsData, loadModulesData, setAPIConfig } from '../actions';

const getQueryParams = (apiConfig) => {
  const { brandIdCMS, siteIdCMS, isAppChannel } = apiConfig || {};
  const isMobileChannel = isAppChannel;
  const channel = isMobileChannel ? MobileChannel : defaultChannel;
  return {
    brand: brandIdCMS || defaultBrand,
    channel,
    country: siteIdCMS || defaultCountry,
  };
};

function* fetchLastUpdated(apiConfig) {
  const { callback } = apiConfig;
  const data = getQueryParams(apiConfig);
  const lastUpdated = yield call(lastUpdatedAbstractor.getData, 'lastUpdated', data, apiConfig);
  if (callback) {
    callback(lastUpdated);
  }
  yield putResolve(setLastUpdated(lastUpdated));
}

const processV2SiteConfigs = (config) => {
  const v2Obj = {};
  const configArray = Object.keys(config);
  configArray.forEach((rcd) => {
    if (rcd.endsWith('_v2')) {
      const modKey = rcd.replace('_v2', '');
      v2Obj[modKey] = config[rcd];
    }
  });
  return { ...config, ...v2Obj };
};

function* fetchXAppConfigData(apiConfig) {
  const xappResponse = yield call(
    xappAbstractor.getData,
    GLOBAL_CONSTANTS.XAPP_CONFIG_MODULE,
    apiConfig
  );
  const { xappConfig: xappForSameBrand, xappConfigOtherBrand: xappForOtherBrands } = xappResponse;
  const xappConfig = processV2SiteConfigs(xappForSameBrand);
  const xappConfigOtherBrand = xappForOtherBrands;
  return { xappConfig, xappConfigOtherBrand };
}

// called only from the web...ignore for mobileapp
function* fetchSiteConfig() {
  const apiConfig = getAPIConfig();
  const stateCookie = readCookie('tcpState');
  const sessionState = yield select((state) => state.session);
  const { xappConfig, xappConfigOtherBrand } = yield fetchXAppConfigData(apiConfig);
  yield put(loadXappConfigData(xappConfig));
  yield put(loadXappConfigDataOtherBrand(xappConfigOtherBrand));
  const session = {
    ...sessionState,
    siteDetails: { ...sessionState.siteDetails, ...xappConfig },
    otherBrandSiteDetails: { ...sessionState.otherBrandSiteDetails, ...xappConfigOtherBrand },
  };
  yield put(setBossBopisFlags(setBossBopisEnabled(stateCookie, session)));
}

function* fetchLabels() {
  const state = yield select();
  const result = yield call(bootstrapAbstractor, '', ['labels'], {}, state, '', null);
  yield put(loadLabelsData(result.labels));
}

function* fetchGlobalHeader() {
  const state = yield select();
  const result = yield call(bootstrapAbstractor, '', ['header'], {}, state, '', null);
  yield put(loadHeaderData(result.header));
  const navigationData = yield call(NavigationAbstractor.getData, {
    module: 'navigation',
    data: {
      depth: 2,
      ignoreCache: true,
    },
  });
  yield put(loadNavigationData(navigationData));
}

function* fetchFooter() {
  const state = yield select();
  const result = yield call(bootstrapAbstractor, '', ['footer'], {}, state, '', null);
  yield put(loadFooterData(result.footer));
}

/* eslint-disable sonarjs/cognitive-complexity */
// eslint-disable-next-line max-statements
export function* bootstrap(params) {
  const {
    payload: {
      name: pageName,
      modules: modulesList,
      apiConfig,
      deviceType,
      siteConfig,
      originalUrl,
    },
  } = params;

  const processedCacheData = {};
  let result = {};

  const { isLabelsDisabledFromRedux } = apiConfig;
  const disableLabelsFromRedux = parseBoolean(isLabelsDisabledFromRedux);
  try {
    if (siteConfig) {
      const { country, currency, language } = apiConfig;

      // putResolve is used to block the other actions till apiConfig is set in state, which is to be used by next bootstrap api calls
      yield putResolve(setDeviceInfo({ deviceType }));
      if (country) {
        yield put(setCountry(country));
      }

      if (currency) {
        yield put(setCurrency({ currency }));
      }

      if (language) {
        yield put(setLanguage(language));
      }

      // handle the async storage orchestration within fetchXAppConfigData
      const { xappConfig, xappConfigOtherBrand } = yield fetchXAppConfigData(apiConfig);
      yield put(loadXappConfigData(xappConfig));
      yield put(loadXappConfigDataOtherBrand(xappConfigOtherBrand));
    }

    const state = yield select();
    result = yield call(
      bootstrapAbstractor,
      pageName,
      modulesList,
      processedCacheData,
      state,
      originalUrl,
      deviceType
    );

    if (result.PLP) {
      const { layout, modules: plpModules, pageName: layoutName, res } = result.PLP;
      yield put(loadLayoutData(layout, layoutName));
      yield put(loadModulesData(plpModules));
      yield put(setPlpProductsDataOnServer(res));
    }
    const findStoreLabel =
      result.labels && result.labels.global && result.labels.global.header
        ? result.labels.global.header.lbl_header_storeDefaultTitle
        : '';

    const isFreeShippingLabel =
      result.labels && result.labels.dynamic.browse_d
        ? result.labels.dynamic.browse_d.lbl_free_shipping_every_day
        : '';

    logger.debug(`Putting labels data:: findStoreLabel: ${findStoreLabel}`);

    if (result && result.labels && !disableLabelsFromRedux) {
      yield put(loadLabelsData(result.labels));
    }
    logger.info(
      'Translation Issue#isFreeShippingLabel',
      isFreeShippingLabel,
      `,language - ${state.APIConfig.language}`,
      `,siteIdCMS - ${state.APIConfig.siteIdCMS}`
    );

    logger.info(
      'Find Store Label-',
      findStoreLabel,
      `,language - ${state.APIConfig.language}`,
      `,siteIdCMS - ${state.APIConfig.siteIdCMS}`
    );

    logger.debug('Putting header data: ', result.header);
    yield put(loadHeaderData(result.header));

    logger.debug('Putting navigation data: ', result.navigation);
    yield put(loadNavigationData(result.navigation));

    logger.debug('Putting footer data: ', result.footer);
    yield put(loadFooterData(result.footer));
  } catch (err) {
    const { bootstrapErrorResponse = null } = err;
    const { bootstrapSagaError } = errorMessages;
    const bootstrapErrorResObj = bootstrapErrorResponse || {
      isErrorInBootstrap: true,
      bootstrapErrorMssg: bootstrapSagaError,
      bootstrapErrorCode: 'Bootstrap-Saga-JS-parsing-Error',
    };
    yield put(setBootstrapError(bootstrapErrorResObj));
    errorHandler({
      errorMsg: bootstrapSagaError,
      component: `Bootstrap API`,
      payload: { params, ...bootstrapErrorResponse },
    });
  }
}

function* setTcpSegment(tcpSegment) {
  try {
    const tcpSegmentValue = tcpSegment.payload;
    yield put(getSetTcpSegment(tcpSegmentValue));
    const tcpSegmentCookieValue = yield call(readCookie, 'tcpSegment');

    if ((tcpSegmentValue && tcpSegmentCookieValue !== tcpSegmentValue) || !tcpSegmentCookieValue) {
      yield call(setCookie, { key: 'tcpSegment', value: tcpSegmentValue });
    }
  } catch (err) {
    errorHandler({
      errorMsg: err,
      component: `Bootstrap - tcpSegment`,
      payload: tcpSegment,
    });
  }
  return null;
}

function* fetchToken() {
  try {
    const { authorization = '' } = yield call(tokenAbstractor.getData);
    if (authorization) {
      yield put(setToken(authorization));
    }
  } catch (err) {
    errorHandler({
      errorMsg: `Error while receiving SFCC token: ${err}`,
      component: `Bootstrap - token`,
    });
  }
  return null;
}

function* fetchServerTime() {
  try {
    const data = yield call(tokenAbstractor.getServerTime);
    if (data) {
      yield put(setServerTime(parseInt(data, 10)));
    }
  } catch (err) {
    errorHandler({
      errorMsg: `Error while fetching servertime: ${err}`,
      component: `Bootstrap - token`,
    });
  }
  return null;
}

function* BootstrapSaga() {
  const cachedServerTimeData = validateReduxCache(fetchServerTime);
  yield takeLatest(GLOBAL_CONSTANTS.BOOTSTRAP_API, bootstrap);
  yield takeLatest(GLOBAL_CONSTANTS.SET_TCP_SEGMENT_METHOD_CALL, setTcpSegment);
  yield takeLatest(GLOBAL_CONSTANTS.FETCH_LABELS, fetchLabels);
  yield takeLatest(GLOBAL_CONSTANTS.SITE_CONFIG, fetchSiteConfig);
  yield takeLatest(GLOBAL_CONSTANTS.FETCH_GLOBAL_HEADER, fetchGlobalHeader);
  yield takeLatest(GLOBAL_CONSTANTS.FETCH_FOOTER, fetchFooter);
  yield takeLatest(GLOBAL_CONSTANTS.FETCH_LAST_UPDATED, fetchLastUpdated);
  yield takeLatest(GLOBAL_CONSTANTS.FETCH_SFCC_AUTH_TOKEN, fetchToken);
  yield takeLeading(GLOBAL_CONSTANTS.GET_SERVER_TIME, cachedServerTimeData);
}

export default BootstrapSaga;
